'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getCreationDetail } from '@/app/apis';
import type { CreationWithTask, ServiceError } from '@/app/types';
import { mergeCreationAndTask } from '@/app/utils';
import { useFetch } from './use-fetch';

type UseCreationDetailNavigationOptions = {
  initialId: string;
  onBeforeCreationChange?: () => void;
};

export function useCreationDetailNavigation({ initialId, onBeforeCreationChange }: UseCreationDetailNavigationOptions) {
  const [activeCreationId, setActiveCreationIdState] = useState(initialId);
  const [currentCreation, setCurrentCreation] = useState<CreationWithTask | undefined>(undefined);
  const [hasRequested, setHasRequested] = useState(false);
  const activeIdRef = useRef(activeCreationId);
  const initialIdRef = useRef(initialId);
  const onBeforeCreationChangeRef = useRef(onBeforeCreationChange);
  const isRouteIdPendingSync = initialIdRef.current !== initialId;
  const visibleActiveCreationId = isRouteIdPendingSync ? initialId : activeCreationId;
  const visibleCurrentCreation =
    isRouteIdPendingSync || currentCreation?.id !== visibleActiveCreationId ? undefined : currentCreation;
  const visibleHasRequested = isRouteIdPendingSync ? false : hasRequested;
  activeIdRef.current = visibleActiveCreationId;
  onBeforeCreationChangeRef.current = onBeforeCreationChange;

  const setActiveCreationId = useCallback((id: string) => {
    onBeforeCreationChangeRef.current?.();
    setCurrentCreation(undefined);
    setHasRequested(false);
    setActiveCreationIdState(id);
  }, []);

  useLayoutEffect(() => {
    if (initialIdRef.current === initialId) return;
    initialIdRef.current = initialId;
    onBeforeCreationChangeRef.current?.();
    setCurrentCreation(undefined);
    setHasRequested(false);
    setActiveCreationIdState(initialId);
  }, [initialId]);

  const {
    error,
    loading,
    run: fetchDetail,
  } = useFetch(
    (id: string) =>
      getCreationDetail({
        path: { id },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (!data?.creation || !data?.task) return;
        if (data.creation.id !== activeIdRef.current) return;
        setCurrentCreation(
          mergeCreationAndTask({
            creation: data.creation,
            task: data.task,
            creationId: data.creation.id,
            extendSourceCreations: data.extend_source_creations,
          }),
        );
      },
    },
  );

  useEffect(() => {
    if (!activeCreationId) return;
    setHasRequested(true);
    fetchDetail(activeCreationId);
  }, [activeCreationId, fetchDetail]);

  const isInitialLoading = useMemo(
    () => !visibleCurrentCreation && (!visibleHasRequested || loading),
    [visibleCurrentCreation, visibleHasRequested, loading],
  );
  const isSwitchLoading = useMemo(
    () => !!visibleCurrentCreation && loading && visibleCurrentCreation.id !== visibleActiveCreationId,
    [visibleCurrentCreation, loading, visibleActiveCreationId],
  );

  return {
    activeCreationId: visibleActiveCreationId,
    currentCreation: visibleCurrentCreation,
    setCurrentCreation,
    loading,
    isInitialLoading,
    isSwitchLoading,
    error: error as unknown as ServiceError | undefined,
    setActiveCreationId,
  };
}
